import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
// import { QRCodeSVG } from "qrcode.react"; 
// [พักใช้งานชั่วคราว] เดิมใช้สร้าง QR แบบ dynamic จาก API
// ตอนนี้เปลี่ยนไปใช้ QR แบบ static ของร้านแม่มณีแทน

import { auth, db, functions } from "../../shared/libs/firebaseConfig";

import "./CoursePaymentPage.css";


// ✅ แนะนำให้นำรูป QR แม่มณีไปไว้ใน public/images/
// เช่น public/images/maemanee-qr.jpg
// const STATIC_QR_IMAGE_URL = "/images/maemanee-qr-chicchic.PNG";
const STATIC_QR_IMAGE_URL = "/images/maemanee-qr-chicchic-dark.JPG";

// ✅ ข้อมูลร้านสำหรับแสดงผลหน้าเว็บ
const STATIC_RECEIVER_DISPLAY_NAME = "CHICCHICLAWTUTOR";
const STATIC_RECEIVER_ACCOUNT_NAME = "ชินะพันธ์ งามวงษ์น้อย";
const STATIC_RECEIVER_CODE = "014000009528217";

function formatBaht(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    maximumFractionDigits: 2,
  }).format(n);
}

function toDateText(ts) {
  try {
    if (!ts) return "-";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("th-TH");
  } catch {
    return "-";
  }
}

function getAllowedHeaderTypes(coursePackage) {
  switch (coursePackage) {
    case "F_2":
    case "P_2":
      return ["civil", "criminal", "summary_civil", "summary_criminal"];

    case "F_1_civil":
    case "P_1_civil":
      return ["civil", "summary_civil"];

    case "F_1_criminal":
    case "P_1_criminal":
      return ["criminal", "summary_criminal"];

    case "L_2":
      return ["civil", "criminal"];
    case "L_1_civil":
      return ["civil"];
    case "L_1_criminal":
      return ["criminal"];

    case "S_2":
      return ["summary_civil", "summary_criminal"];
    case "S_1_civil":
      return ["summary_civil"];
    case "S_1_criminal":
      return ["summary_criminal"];

    default:
      return [];
  }
}

function getFileExt(fileName = "") {
  const parts = String(fileName).split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "jpg";
}

function buildLearnUrl(courseId) {
  return `/learn/${courseId}`;
}

function addDays(baseDate, days) {
  const d = new Date(baseDate);
  d.setDate(d.getDate() + days);
  return d;
}

function toSafeDate(value) {
  try {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function buildInstallmentsFromEnroll(enrollDoc, courseDoc) {
  const totalAmount = Number(enrollDoc?.amount || 0);
  const totalInstallments = Number(courseDoc?.maxInstallments || 0);
  const createdAt =
    toSafeDate(enrollDoc?.createdAt) ||
    toSafeDate(enrollDoc?.installmentPlan?.acceptedAt) ||
    new Date();

  if (!courseDoc?.allowInstallment || totalInstallments < 2 || totalAmount <= 0) {
    return [];
  }

  // ใช้ floor แล้วไปบวกเศษงวดสุดท้าย เพื่อให้ยอดรวมตรงเสมอ
  const baseAmount = Math.floor(totalAmount / totalInstallments);
  const remainder = totalAmount - (baseAmount * totalInstallments);

  return Array.from({ length: totalInstallments }, (_, index) => {
    const installmentNo = index + 1;
    const dueAt = addDays(createdAt, index * 30);

    return {
      installmentNo,
      amount:
        installmentNo === totalInstallments
          ? baseAmount + remainder
          : baseAmount,
      dueAt,
      status: "pending",
    };
  });
}

function getInstallmentList(enrollDoc, courseDoc) {
  const raw = Array.isArray(enrollDoc?.installments) ? enrollDoc.installments : [];
  if (raw.length > 0) {
    return raw.map((item, index) => ({
      installmentNo: Number(item?.installmentNo || index + 1),
      amount: Number(item?.amount || 0),
      dueAt: toSafeDate(item?.dueAt),
      status: item?.status || "pending",
      paidAt: item?.paidAt || null,
      paymentChannel: item?.paymentChannel || "",
      paymentMethod: item?.paymentMethod || "",
      slipImagePath: item?.slipImagePath || "",
      qrDataPersonal: item?.qrDataPersonal || null,
      slipVerifiedData: item?.slipVerifiedData || null,
    }));
  }

  return buildInstallmentsFromEnroll(enrollDoc, courseDoc);
}

function getEffectivePaymentType(enrollDoc) {
  return enrollDoc?.paymentType || "full";
}

function getCurrentInstallment(installments = []) {
  return installments.find((item) => item?.status !== "paid") || null;
}

function isInstallmentOverdue(installment) {
  if (!installment?.dueAt || installment?.status === "paid") return false;
  const due = toSafeDate(installment.dueAt);
  if (!due) return false;
  return Date.now() > due.getTime();
}

function formatThaiDateOnly(value) {
  try {
    const d = toSafeDate(value);
    if (!d) return "-";
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "-";
  }
}

export default function CoursePayment() {
  const navigate = useNavigate();
  const { courseId, enrollId } = useParams();

  const [loadingPage, setLoadingPage] = useState(true);
  const [savingBasic, setSavingBasic] = useState(false);
  const [uploadingSlip, setUploadingSlip] = useState(false);
  const [verifyingSlip, setVerifyingSlip] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [course, setCourse] = useState(null);
  const [enroll, setEnroll] = useState(null);

  const [selectedPaymentType, setSelectedPaymentType] = useState("full");

  // ตอนนี้ให้ใช้โหมด API เป็นหลักเหมือนเดิม
  // แต่ QR เปลี่ยนจาก dynamic -> static
  const [paymentMode, setPaymentMode] = useState("api");

  const [slipFile, setSlipFile] = useState(null);
  const [slipPreviewUrl, setSlipPreviewUrl] = useState("");
  const [uploadedSlipUrl, setUploadedSlipUrl] = useState("");

  // [พักใช้งานชั่วคราว]
  // เดิมใช้เก็บข้อมูล QR ที่สร้างจาก Cloud Function / Slip2Go
  // ตอนนี้เปลี่ยนเป็น QR แบบ static ของร้านแม่มณี
  /*
  const [qrData, setQrData] = useState({
    qrCode: "",
    accountName: "",
    amount: "",
  });
  */

  const FACEBOOK_PAGE_URL = "https://facebook.com/chicchiclawtutor";

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      setLoadingPage(true);
      setError("");

      try {
        if (!courseId || !enrollId) {
          throw new Error("ไม่พบข้อมูลการชำระเงิน");
        }

        const currentUser = auth.currentUser;
        if (!currentUser?.uid) {
          throw new Error("กรุณาเข้าสู่ระบบก่อน");
        }

        const enrollRef = doc(db, "enrolls", enrollId);
        const enrollSnap = await getDoc(enrollRef);

        if (!enrollSnap.exists()) {
          throw new Error("ไม่พบข้อมูลการสมัครเรียน");
        }

        // const enrollData = { id: enrollSnap.id, ...enrollSnap.data() };
        const enrollData = {
          id: enrollSnap.id,
          ...enrollSnap.data(),
          paymentType: enrollSnap.data()?.paymentType || "full",
        };

        if (String(enrollData.courseId || "") !== String(courseId || "")) {
          throw new Error("ข้อมูลคอร์สและการสมัครไม่ตรงกัน");
        }

        const courseRef = doc(db, "courses", courseId);
        const courseSnap = await getDoc(courseRef);

        if (!courseSnap.exists()) {
          throw new Error("ไม่พบข้อมูลคอร์ส");
        }

        const courseData = { id: courseSnap.id, ...courseSnap.data() };

        if (!mounted) return;

        setEnroll(enrollData);
        setCourse(courseData);

        if (courseData?.allowInstallment) {
          setSelectedPaymentType(enrollData?.paymentType || "full");
        } else {
          setSelectedPaymentType("full");
        }

        // [พักใช้งานชั่วคราว]
        // ถ้าในอนาคตกลับไปใช้ dynamic QR สามารถเปิดส่วนนี้ได้
        /*
        if (enrollData?.qrData?.qrCode) {
          setQrData({
            qrCode: enrollData.qrData.qrCode || "",
            accountName: enrollData.qrData.accountName || "",
            amount: enrollData.qrData.amount || "",
          });
        }
        */

        setPaymentMode(courseData?.paymentApiEnabled === false ? "manual" : "api");
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(err?.message || "โหลดข้อมูลไม่สำเร็จ");
      } finally {
        if (mounted) setLoadingPage(false);
      }
    }

    loadData();

    return () => {
      mounted = false;
      if (slipPreviewUrl) {
        URL.revokeObjectURL(slipPreviewUrl);
      }
    };
  }, [courseId, enrollId]);

  // [พักใช้งานชั่วคราว]
  // เดิมใช้สร้าง QR ตามยอดคอร์สผ่าน Cloud Function
  // ตอนนี้เปลี่ยนไปใช้ QR static ของร้านแม่มณีแทน
  /*
  useEffect(() => {
    async function ensureQr() {
      if (!enroll?.id || !course?.id) return;
      if (paymentMode !== "api") return;
      if (enroll?.paymentStatus === "paid" && enroll?.status === "active") return;
      if (qrData.qrCode) return;

      try {
        setLoadingQr(true);
        const createQrFn = httpsCallable(functions, "createEnrollPromptPayQRCode");
        const res = await createQrFn({
          enrollId: enroll.id,
          courseId: course.id,
        });

        const data = res?.data || {};
        setQrData({
          qrCode: data?.qrCode || "",
          accountName: data?.accountName || "",
          amount: data?.amount || "",
        });
      } catch (err) {
        console.error(err);
        setError(err?.message || "สร้าง QR PromptPay ไม่สำเร็จ");
      } finally {
        setLoadingQr(false);
      }
    }

    ensureQr();
  }, [enroll, course, paymentMode, qrData.qrCode]);
  */

  /* const amountText = useMemo(() => {
    return formatBaht(enroll?.amount || 0);
  }, [enroll]); */
  const allowInstallment = useMemo(() => {
    return !!course?.allowInstallment;
  }, [course]);

  const effectivePaymentType = useMemo(() => {
    if (!allowInstallment) return "full";
    return selectedPaymentType || getEffectivePaymentType(enroll);
  }, [allowInstallment, selectedPaymentType, enroll]);

  const installmentList = useMemo(() => {
    return getInstallmentList(enroll, course);
  }, [enroll, course]);

  const currentInstallment = useMemo(() => {
    if (effectivePaymentType !== "installment") return null;
    return getCurrentInstallment(installmentList);
  }, [effectivePaymentType, installmentList]);

  const currentInstallmentNo = useMemo(() => {
    return Number(currentInstallment?.installmentNo || 0);
  }, [currentInstallment]);

  const currentPayAmount = useMemo(() => {
    if (effectivePaymentType === "installment") {
      return Number(currentInstallment?.amount || 0);
    }
    return Number(enroll?.amount || 0);
  }, [effectivePaymentType, currentInstallment, enroll]);

  const amountText = useMemo(() => {
    return formatBaht(currentPayAmount || 0);
  }, [currentPayAmount]);

  const currentInstallmentOverdue = useMemo(() => {
    return isInstallmentOverdue(currentInstallment);
  }, [currentInstallment]);

  const allowedHeaderTypes = useMemo(() => {
    return getAllowedHeaderTypes(enroll?.coursePackage || "");
  }, [enroll]);

  /* const canStartLearningNow = useMemo(() => {
    return (
        enroll?.paymentStatus === "paid" &&
        enroll?.status === "active" &&
        enroll?.accessStatus === "granted"
    );
  }, [enroll]); */
  const canStartLearningNow = useMemo(() => {
    const paymentStatus = enroll?.paymentStatus || "";
    const status = enroll?.status || "";
    const accessStatus = enroll?.accessStatus || "";

    const paymentOk =
      paymentStatus === "paid" || paymentStatus === "partial_paid";

    return paymentOk && status === "active" && accessStatus === "granted";
  }, [enroll]);

  const isBusy = savingBasic || uploadingSlip || verifyingSlip || redirecting;

  const onClickGoLearn = () => {
    if (!course?.id) return;
    navigate(buildLearnUrl(course.id));
  };

  const onSelectInstallmentPlan = async () => {
  if (!enroll?.id || !course?.id) return;

  try {
    setError("");
    setSuccessMsg("");

    const generatedInstallments = buildInstallmentsFromEnroll(enroll, course);
    if (!generatedInstallments.length) {
      throw new Error("ไม่สามารถสร้างแผนผ่อนชำระได้");
    }

    await updateDoc(doc(db, "enrolls", enroll.id), {
      paymentType: "installment",
      paymentStatus: "pending_installment",
      status: "pending_payment",
      installmentPlan: {
        enabled: true,
        acceptedAt: serverTimestamp(),
        totalAmount: Number(enroll?.amount || 0),
        totalInstallments: Number(course?.maxInstallments || 0),
        installmentAmount: Number(generatedInstallments?.[0]?.amount || 0),
        status: "ongoing",
      },
      installments: generatedInstallments.map((item) => ({
        installmentNo: item.installmentNo,
        amount: item.amount,
        dueAt: item.dueAt,
        status: item.status,
        paidAt: null,
        paymentChannel: "",
        paymentMethod: "",
        slipImagePath: "",
        qrDataPersonal: null,
        slipVerifiedData: null,
      })),
      updatedAt: serverTimestamp(),
    });

    setEnroll((prev) => ({
      ...prev,
      paymentType: "installment",
      paymentStatus: "pending_installment",
      status: "pending_payment",
      installmentPlan: {
        enabled: true,
        totalAmount: Number(prev?.amount || 0),
        totalInstallments: Number(course?.maxInstallments || 0),
        installmentAmount: Number(generatedInstallments?.[0]?.amount || 0),
        status: "ongoing",
      },
      installments: generatedInstallments.map((item) => ({
        installmentNo: item.installmentNo,
        amount: item.amount,
        dueAt: item.dueAt,
        status: item.status,
        paidAt: null,
        paymentChannel: "",
        paymentMethod: "",
        slipImagePath: "",
        qrDataPersonal: null,
        slipVerifiedData: null,
      })),
    }));

    setSelectedPaymentType("installment");
    setSuccessMsg("เลือกรูปแบบผ่อนชำระแล้ว กรุณาชำระงวดปัจจุบัน");
  } catch (err) {
    console.error(err);
    setError(err?.message || "ตั้งค่าผ่อนชำระไม่สำเร็จ");
  }
};

  // [พักใช้งานชั่วคราว]
  // เดิมใช้รีเฟรช QR แบบ dynamic จาก API
  /*
  const onRefreshQr = async () => {
    try {
      setError("");
      setSuccessMsg("");
      setLoadingQr(true);

      const createQrFn = httpsCallable(functions, "createEnrollPromptPayQRCode");
      const res = await createQrFn({
        enrollId: enroll.id,
        courseId: course.id,
        forceRefresh: true,
      });

      const data = res?.data || {};
      setQrData({
        qrCode: data?.qrCode || "",
        accountName: data?.accountName || "",
        amount: data?.amount || "",
      });

      setSuccessMsg("รีเฟรช QR สำเร็จ");
    } catch (err) {
      console.error(err);
      setError(err?.message || "รีเฟรช QR ไม่สำเร็จ");
    } finally {
      setLoadingQr(false);
    }
  };
  */

  const onSaveManualIntent = async () => {
    if (!enroll?.id) return;

    try {
      setSavingBasic(true);
      setError("");
      setSuccessMsg("");

      await updateDoc(doc(db, "enrolls", enroll.id), {
        paymentMethod: "manual_transfer",
        paymentChannel: "manual_qr",
        paymentStatus: "awaiting_admin_review",
        status: "pending_review",
        manualPaymentNoticeAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setEnroll((prev) => ({
        ...prev,
        paymentMethod: "manual_transfer",
        paymentChannel: "manual_qr",
        paymentStatus: "awaiting_admin_review",
        status: "pending_review",
      }));

      setSuccessMsg(
        "ระบบบันทึกข้อมูลการแจ้งชำระแบบพื้นฐานแล้ว กรุณาส่งหลักฐานไปที่เพจเพื่อรอแอดมินตรวจสอบ"
      );
    } catch (err) {
      console.error(err);
      setError(err?.message || "บันทึกข้อมูลไม่สำเร็จ");
    } finally {
      setSavingBasic(false);
    }
  };

  const onSelectSlipFile = (e) => {
    const file = e.target.files?.[0] || null;
    setSlipFile(file);
    setUploadedSlipUrl("");

    if (slipPreviewUrl) {
      URL.revokeObjectURL(slipPreviewUrl);
      setSlipPreviewUrl("");
    }

    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setSlipPreviewUrl(previewUrl);
    }
  };

  const uploadSlipToStorage = async ({ file, enrollDoc, courseDoc }) => {
    const currentUser = auth.currentUser;
    if (!currentUser?.uid) {
      throw new Error("กรุณาเข้าสู่ระบบก่อน");
    }
    if (!file) {
      throw new Error("กรุณาเลือกไฟล์สลิป");
    }
    if (!enrollDoc?.id || !courseDoc?.id) {
      throw new Error("ไม่พบข้อมูลที่จำเป็นสำหรับการอัปโหลด");
    }

    setUploadingSlip(true);

    try {
      const storage = getStorage();
      const ext = getFileExt(file.name);
      const storagePath = `payment-slips/${currentUser.uid}/${enrollDoc.id}/${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file, {
        contentType: file.type || "image/jpeg",
        customMetadata: {
          userId: currentUser.uid,
          enrollId: enrollDoc.id,
          courseId: courseDoc.id,
        },
      });

      const downloadURL = await getDownloadURL(storageRef);
      setUploadedSlipUrl(downloadURL);

      return {
        storagePath,
        downloadURL,
      };
    } finally {
      setUploadingSlip(false);
    }
  };

  /* const verifySlipViaCloudFunction = async ({
    enrollDoc,
    courseDoc,
    storagePath,
  }) => {
    setVerifyingSlip(true);

    try {
      const verifyFn = httpsCallable(functions, "verifyEnrollPaymentSlip");
      const res = await verifyFn({
        enrollId: enrollDoc.id,
        courseId: courseDoc.id,
        storagePath,
        expectedAmount: enrollDoc.amount,
      });

      return res?.data || null;
    } finally {
      setVerifyingSlip(false);
    }
  }; */
  const verifySlipViaCloudFunction = async ({
    enrollDoc,
    courseDoc,
    storagePath,
    expectedAmount,
    installmentNo = 0,
  }) => {
    setVerifyingSlip(true);

    try {
      const usePersonalFlow =
        !!enrollDoc?.qrDataPersonal || courseDoc?.paymentApiEnabled !== false;

      const fnName = usePersonalFlow
        ? "verifyEnrollPaymentSlipPersonal"
        : "verifyEnrollPaymentSlip";

      const verifyFn = httpsCallable(functions, fnName);

      const res = await verifyFn({
        enrollId: enrollDoc.id,
        courseId: courseDoc.id,
        storagePath,
        expectedAmount,
        installmentNo,
        paymentType: effectivePaymentType,
      });

      return res?.data || null;
    } finally {
      setVerifyingSlip(false);
    }
  };

  const onVerifyByEasySlip = async (e) => {
    e.preventDefault();

    try {
      setError("");
      setSuccessMsg("");

      if (!enroll?.id || !course?.id) {
        throw new Error("ไม่พบข้อมูลการสมัครเรียน");
      }

      if (!slipFile) {
        throw new Error("กรุณาอัปโหลดหลักฐานการชำระเงิน");
      }

      const { storagePath, downloadURL } = await uploadSlipToStorage({
        file: slipFile,
        enrollDoc: enroll,
        courseDoc: course,
      });

      /* const result = await verifySlipViaCloudFunction({
        enrollDoc: enroll,
        courseDoc: course,
        storagePath,
      }); */
      const result = await verifySlipViaCloudFunction({
        enrollDoc: enroll,
        courseDoc: course,
        storagePath,
        expectedAmount: currentPayAmount,
        installmentNo: currentInstallmentNo,
      });

      /* setEnroll((prev) => ({
        ...prev,
        paymentMethod: "easyslip",
        paymentChannel: "promptpay_qr_static",
        paymentStatus: result?.paymentStatus || "paid",
        status: result?.status || "active",
        accessStatus: result?.accessStatus || "granted",
        allowedHeaderTypes: result?.allowedHeaderTypes || allowedHeaderTypes,
        slipImagePath: storagePath,
        slipImageURL: downloadURL,
      })); */
      setEnroll((prev) => {
        const next = {
          ...prev,
          paymentMethod: "easyslip",
          paymentChannel:
            effectivePaymentType === "installment"
              ? "promptpay_qr_dynamic_personal_installment"
              : "promptpay_qr_dynamic_personal",
          paymentStatus:
            result?.paymentStatus ||
            (effectivePaymentType === "installment" ? "partial_paid" : "paid"),
          status: result?.status || "active",
          accessStatus: result?.accessStatus || "granted",
          allowedHeaderTypes: result?.allowedHeaderTypes || allowedHeaderTypes,
          slipImagePath: storagePath,
          slipImageURL: downloadURL,
        };

        if (effectivePaymentType !== "installment") {
          return next;
        }

        const items = getInstallmentList(prev, course).map((item) => {
          if (Number(item.installmentNo) !== Number(currentInstallmentNo)) return item;
          return {
            ...item,
            status: "paid",
            paidAt: new Date(),
            paymentChannel: "promptpay_qr_dynamic_personal_installment",
            paymentMethod: "easyslip",
            slipImagePath: storagePath,
          };
        });

        const hasUnpaid = items.some((item) => item.status !== "paid");

        return {
          ...next,
          installments: items,
          paymentType: "installment",
          paymentStatus: hasUnpaid ? "partial_paid" : "paid",
          status: "active",
          accessStatus: "granted",
        };
      });

      // setSuccessMsg("ตรวจสอบการชำระเงินสำเร็จ ระบบกำลังพาไปหน้าเรียน...");
      setSuccessMsg(
        effectivePaymentType === "installment"
          ? `ตรวจสอบการชำระเงินงวดที่ ${currentInstallmentNo} สำเร็จ ระบบกำลังพาไปหน้าเรียน...`
          : "ตรวจสอบการชำระเงินสำเร็จ ระบบกำลังพาไปหน้าเรียน..."
      );
      setRedirecting(true);

      setTimeout(() => {
        navigate(buildLearnUrl(course.id));
      }, 800);
    } catch (err) {
      console.error(err);
      setError(err?.message || "ตรวจสอบสลิปไม่สำเร็จ");
    } finally {
      setRedirecting(false);
    }
  };

  if (loadingPage) {
    return (
      <div className="container py-4">
        <div className="d-flex align-items-center gap-2 text-secondary">
          <div className="spinner-border spinner-border-sm" role="status" />
          <span>กำลังโหลดข้อมูลการชำระเงิน...</span>
        </div>
      </div>
    );
  }

  if (error && !enroll) {
    return (
      <div className="container py-4">
        <Link to="/courses" className="text-decoration-none">
          ← กลับไปหน้าคอร์สทั้งหมด
        </Link>
        <div className="alert alert-danger mt-3 mb-0">{error}</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="mb-4">
        {/* <Link to={`/courses/${courseId}`} className="text-decoration-none">
          ← กลับไปหน้ารายละเอียดคอร์ส
        </Link> */}

        <h1 className="h4 mt-2 mb-1">ชำระเงินคอร์สเรียน</h1>
        <div className="text-secondary">
          {enroll?.courseTitle || course?.title || "-"}
        </div>
      </div>

      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {canStartLearningNow && (
        <div className="alert alert-success d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>การชำระเงินเสร็จสมบูรณ์แล้ว คุณสามารถเริ่มเรียนได้ทันที</div>
          <button className="btn btn-success" onClick={onClickGoLearn}>
            ไปหน้าเรียน
          </button>
        </div>
      )}

      <div className="row g-4 align-items-start">
        <div className="col-lg-7">
          <div className="card app-card course-payment-card">
            <div className="card-body p-4">
              <h2 className="h6 mb-3">ข้อมูลการสมัครเรียน</h2>

              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-secondary">คอร์ส</span>
                <span className="text-end">{enroll?.courseTitle || "-"}</span>
              </div>

              {!!enroll?.coursePackage && (
                <div className="d-flex justify-content-between gap-3 mb-2">
                  <span className="text-secondary">แพ็กเกจ</span>
                  <span className="text-end">
                    {enroll?.coursePackageLabel || enroll?.coursePackage}
                  </span>
                </div>
              )}

              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-secondary">ผู้สมัคร</span>
                <span className="text-end">
                  {enroll?.userSnapshot?.fullName || "-"}
                </span>
              </div>

              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-secondary">สถานะการสมัคร</span>
                <span className="text-end">{enroll?.status || "-"}</span>
              </div>

              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-secondary">สถานะการชำระเงิน</span>
                <span className="text-end">{enroll?.paymentStatus || "-"}</span>
              </div>

              <div className="d-flex justify-content-between gap-3">
                <span className="text-secondary">สร้างรายการเมื่อ</span>
                <span className="text-end">{toDateText(enroll?.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* <div className="card app-card course-payment-card mb-4">
            <div className="card-body p-4">
              <h2 className="h6 mb-3">โหมดการชำระเงิน</h2>

              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`btn ${
                    paymentMode === "api" ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setPaymentMode("api")}
                  disabled={isBusy}
                >
                  ชำระผ่าน QR + ตรวจสลิปอัตโนมัติ
                </button>

                <button
                  type="button"
                  className={`btn ${
                    paymentMode === "manual"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => setPaymentMode("manual")}
                  disabled={isBusy}
                >
                  วิธีพื้นฐาน
                </button>
              </div>

              <div className="text-secondary small mt-2">
                สแกน QR ชำระเงิน และให้ระบบตรวจสลิปอัตโนมัติหลังอัปโหลด
              </div>
            </div>
          </div> */}
          {allowInstallment && (
            <div className="card app-card course-payment-card mb-4">
              <div className="card-body p-4">
                <h2 className="h6 mb-3">เลือกรูปแบบการชำระเงิน</h2>

                <div className="d-flex flex-wrap gap-2 mb-2">
                  <button
                    type="button"
                    className={`btn ${
                      effectivePaymentType === "full" ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => setSelectedPaymentType("full")}
                    disabled={isBusy || enroll?.paymentType === "installment"}
                  >
                    ชำระเต็มจำนวน
                  </button>

                  <button
                    type="button"
                    className={`btn ${
                      effectivePaymentType === "installment"
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => {
                      if (enroll?.paymentType === "installment") {
                        setSelectedPaymentType("installment");
                        return;
                      }
                      onSelectInstallmentPlan();
                    }}
                    disabled={isBusy}
                  >
                    ผ่อนชำระ
                  </button>
                </div>

                <div className="text-secondary small">
                  คอร์สนี้รองรับการผ่อนชำระได้สูงสุด {course?.maxInstallments || 0} งวด
                </div>

                {effectivePaymentType === "installment" && installmentList.length > 0 && (
                  <div className="mt-3">
                    <div className="fw-semibold mb-2">ตารางงวดชำระ</div>

                    <div className="d-flex flex-column gap-2">
                      {installmentList.map((item) => (
                        <div
                          key={item.installmentNo}
                          className="d-flex justify-content-between align-items-center gap-3 border rounded p-3"
                        >
                          <div>
                            <div className="fw-semibold">งวดที่ {item.installmentNo}</div>
                            <div className="small text-secondary">
                              ครบกำหนด {formatThaiDateOnly(item.dueAt)}
                            </div>
                          </div>

                          <div className="text-end">
                            <div className="fw-semibold">{formatBaht(item.amount)}</div>
                            <div className="small text-secondary">
                              {item.status === "paid" ? "ชำระแล้ว" : "รอชำระ"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {paymentMode === "api" && (
            <div className="card app-card course-payment-card">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <h2 className="h6 mb-0">ชำระผ่าน QR + ตรวจสลิปอัตโนมัติ</h2>
                  <span className="badge text-bg-success">พร้อมใช้งาน</span>
                </div>

                {/* <div className="alert course-payment-alert-light">
                  กรุณาสแกนชำระเงินด้วย QR ด้านล่างตามยอดของคอร์สนี้
                  แล้วอัปโหลดสลิปเพื่อให้ระบบตรวจสอบและเปิดสิทธิเข้าเรียนอัตโนมัติ
                </div> */}
                <div className="alert course-payment-alert-light">
                  {effectivePaymentType === "installment" && currentInstallment ? (
                    <>
                      กรุณาชำระเงินงวดที่ <strong>{currentInstallment.installmentNo}</strong>{" "}
                      จำนวน <strong>{formatBaht(currentInstallment.amount)}</strong>{" "}
                      ภายในวันที่{" "}
                      <strong>{formatThaiDateOnly(currentInstallment.dueAt)}</strong>
                      {currentInstallmentOverdue ? (
                        <div className="text-danger mt-2">
                          งวดนี้เลยกำหนดชำระแล้ว กรุณาชำระเพื่อใช้งานต่อเนื่อง
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      กรุณาสแกนชำระเงินด้วย QR ด้านล่างตามยอดของคอร์สนี้
                      แล้วอัปโหลดสลิปเพื่อให้ระบบตรวจสอบและเปิดสิทธิเข้าเรียนอัตโนมัติ
                    </>
                  )}
                </div>

                <div className="row g-4 align-items-start">
                  <div className="col-md-6">
                    <div className="course-payment-box rounded p-3 text-center">
                      <div className="fw-semibold mb-2">QR PromptPay ร้านค้า</div>

                      <div className="d-flex justify-content-center">
                        <img
                          src={STATIC_QR_IMAGE_URL}
                          alt="QR PromptPay ร้านค้า"
                          className="img-fluid rounded"
                          style={{ maxWidth: 320, width: "100%" }}
                        />
                      </div>

                     {/*  <div className="small text-secondary mt-3">
                        ร้านค้า: {STATIC_RECEIVER_DISPLAY_NAME}
                      </div>

                      <div className="small text-secondary mt-1">
                        ชื่อบัญชี: {STATIC_RECEIVER_ACCOUNT_NAME}
                      </div>

                      <div className="small text-secondary mt-1">
                        รหัสร้านค้า: {STATIC_RECEIVER_CODE}
                      </div> */}

                      <div className="mt-3">
                        {/* <div className="small text-secondary">ยอดที่ต้องโอน</div>
                        <div className="fw-semibold fs-4">{amountText}</div> */}
                        <div className="small text-secondary">
                          {effectivePaymentType === "installment" && currentInstallment
                            ? `ยอดที่ต้องโอน (งวดที่ ${currentInstallment.installmentNo})`
                            : "ยอดที่ต้องโอน"}
                        </div>
                        <div className="fw-semibold fs-4">{amountText}</div>
                      </div>

                      <div className="small text-danger mt-2">
                        โปรดตรวจสอบยอดและกรอกจำนวนเงินก่อนชำระ
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <form onSubmit={onVerifyByEasySlip}>
                      <div className="mb-3">
                        <label className="form-label">
                          อัปโหลดหลักฐานการชำระเงิน
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={onSelectSlipFile}
                          disabled={isBusy}
                        />
                        <div className="form-text">
                          รองรับไฟล์รูปภาพ เช่น jpg, jpeg, png
                        </div>
                      </div>

                      {slipPreviewUrl && (
                        <div className="mb-3">
                          <div className="small text-secondary mb-2">
                            ตัวอย่างไฟล์ที่เลือก
                          </div>
                          <img
                            src={slipPreviewUrl}
                            alt="slip preview"
                            className="img-fluid rounded border-theme"
                            style={{ maxHeight: 360 }}
                          />
                        </div>
                      )}

                      {uploadedSlipUrl && (
                        <div className="alert alert-light border small">
                          อัปโหลดสลิปแล้วเรียบร้อย
                        </div>
                      )}

                      <div className="d-grid">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isBusy}
                        >
                          {uploadingSlip ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              กำลังอัปโหลดสลิป...
                            </>
                          ) : verifyingSlip ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              กำลังตรวจสอบสลิป...
                            </>
                          ) : redirecting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              กำลังพาไปหน้าเรียน...
                            </>
                          ) : (
                            "ตรวจสอบสลิปและเริ่มเรียน"
                          )}
                        </button>
                      </div>

                      <div className="text-secondary small mt-2">
                        ระบบจะตรวจสอบยอดเงินและข้อมูลผู้รับเงินจากสลิป หากถูกต้องจะเปิดสิทธิเข้าเรียนให้อัตโนมัติ
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {paymentMode === "manual" && (
            <div className="card app-card course-payment-card">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <h2 className="h6 mb-0">
                    วิธีพื้นฐาน (กรณีระบบตรวจสลิปมีปัญหา)
                  </h2>
                  <span className="badge text-bg-warning">สำรอง</span>
                </div>

                <div className="alert alert-warning">
                  ขอบคุณสำหรับการสมัครคอร์สเรียน{" "}
                  <strong>{enroll?.courseTitle || "-"}</strong> โปรดแจ้งการสมัครเรียนพร้อมส่งหลักฐานการโอนเงินได้ที่เพจ{" "}
                  <a
                    href={FACEBOOK_PAGE_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Facebook Page
                  </a>{" "}
                  ซึ่งแอดมินจะตรวจสอบข้อมูลการสมัครและผู้เรียนสามารถเข้าเรียนได้ภายใน 4 ชั่วโมง
                </div>

                <div className="course-payment-box rounded p-3">
                  <div className="fw-semibold mb-2">ขั้นตอน</div>
                  <ol className="mb-3">
                    <li>ติดต่อทีมงานที่เพจ</li>
                    <li>แจ้งชื่อผู้สมัครและชื่อคอร์ส</li>
                    <li>ส่งหลักฐานการโอนเงินให้แอดมิน</li>
                    <li>รอแอดมินอนุมัติสิทธิ์เข้าเรียน</li>
                  </ol>

                  <div className="d-grid gap-2">
                    <a
                      href={FACEBOOK_PAGE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-primary"
                    >
                      ไปที่เพจ Facebook
                    </a>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={onSaveManualIntent}
                      disabled={isBusy}
                    >
                      {savingBasic ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          กำลังบันทึก...
                        </>
                      ) : (
                        "บันทึกว่าเลือกชำระแบบพื้นฐาน"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="col-lg-5">
          <div className="card app-card course-payment-card">
            <div className="card-body p-4">
              <div className="fw-semibold mb-3">สรุปยอดชำระ</div>

              <div className="d-flex justify-content-between gap-3">
                <span className="text-secondary">คอร์ส</span>
                <span className="text-end">{enroll?.courseTitle || "-"}</span>
              </div>

              {!!enroll?.coursePackage && (
                <div className="d-flex justify-content-between gap-3 mt-2">
                  <span className="text-secondary">แพ็กเกจ</span>
                  <span className="text-end">
                    {enroll?.coursePackageLabel || enroll?.coursePackage}
                  </span>
                </div>
              )}

              {/* <div className="d-flex justify-content-between gap-3 mt-2">
                <span className="text-secondary">สิทธิที่ควรได้</span>
                <span className="text-end">
                  {allowedHeaderTypes.length > 0
                    ? allowedHeaderTypes.join(", ")
                    : "ทั้งคอร์ส / ทั่วไป"}
                </span>
              </div> */}

              <hr />

              {/* <div className="d-flex justify-content-between align-items-center">
                <span className="text-secondary">ยอดรวม</span>
                <span className="fw-semibold fs-5">{amountText}</span>
              </div> */}
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-secondary">
                  {effectivePaymentType === "installment" && currentInstallment
                    ? `ยอดงวดที่ ${currentInstallment.installmentNo}`
                    : "ยอดรวม"}
                </span>
                <span className="fw-semibold fs-5">{amountText}</span>
              </div>

              {effectivePaymentType === "installment" && (
                <div className="text-secondary small mt-3">
                  ยอดรวมทั้งคอร์ส {formatBaht(enroll?.amount || 0)} •
                  ผ่อนสูงสุด {course?.maxInstallments || 0} งวด
                </div>
              )}

              {/* <div className="text-secondary small mt-3">
                เมื่ออัปโหลดสลิปและตรวจสอบสำเร็จ ระบบจะเปิดสิทธิ์เข้าเรียนให้อัตโนมัติ
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}